import { useTheme } from "@mui/material";
import { useFormatAmount } from "../../../shared";

// ApexCharts injects custom tooltips as raw HTML, so user-entered text must be escaped
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Series colours come from the app theme (palette.chart). Each metric keeps the
// same colour across bars, lines and tooltips; text stays in neutral ink.
export const useChartColors = () => {
  const theme = useTheme();
  return {
    gross: theme.palette.chart.series1,
    net: theme.palette.chart.series2,
    atv: theme.palette.chart.series3,
    transactions: theme.palette.chart.series4,
    title: theme.palette.neutral[900],
    label: theme.palette.neutral[700],
    surface: theme.palette.background.paper,
  };
};

// Options shared by every dashboard chart so they all read the same way:
// app font, hairline horizontal gridlines, muted axis text, no toolbar.
// Charts spread this first and then add their own axes and marks.
export const useBaseChartOptions = () => {
  const theme = useTheme();
  const axisLabelStyle = {
    colors: theme.palette.text.secondary,
    fontSize: "12px",
  };
  return {
    chart: {
      fontFamily: theme.typography.fontFamily,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    grid: {
      show: true,
      borderColor: theme.palette.divider,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 16, right: 16, left: 8 },
    },
    xaxis: {
      axisBorder: { show: true, color: theme.palette.divider },
      axisTicks: { show: false },
      labels: {
        style: axisLabelStyle,
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        trim: false,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: { style: axisLabelStyle },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { marker: { show: false } },
  };
};

// Cap the number of x-axis labels so day labels never crowd each other;
// ApexCharts then shows every nth category label
export const getXAxisTickAmount = (pointCount) => Math.min(pointCount, 10);

// The tooltip is rendered by ApexCharts outside React, so the tenant currency
// settings and theme colours are captured here and baked into the renderer
export const useSalesTooltipRenderer = () => {
  const formatAmount = useFormatAmount();
  const colors = useChartColors();
  // text is ink for readability; the series colour sits beside it as a dot
  const row = (color, label, value) =>
    `<div style="display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:${colors.label};">
      <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};flex:none;"></span>
      <span>${label}: ${value}</span>
    </div>`;
  return (title, item, grossKey, netKey) =>
    `<div style="padding:8px 10px;display:grid;gap:4px;">
      <div style="font-size:12px;font-weight:700;color:${colors.title};margin-bottom:2px;">${escapeHtml(
        title
      )}</div>
      ${row(colors.gross, "Gross Sales", formatAmount(item[grossKey]))}
      ${row(colors.net, "Net Sales", formatAmount(item[netKey]))}
      ${row(colors.atv, "ATV", formatAmount(item.atv))}
      ${row(
        colors.transactions,
        "Transactions",
        Number(item.transaction_count || 0).toLocaleString()
      )}
    </div>`;
};
